import { formatJobNumber } from '@fieldops/shared'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { Icon } from '../Icon.js'
import { combineLabel, dayLabel, leadLabel } from '../repeat/rule.js'
import { Button } from '../ui.js'
import type { AgreementPage, ReviewActions } from './AgreementGrid.js'
import { siteTitle } from './agreement-view.js'
import { TickBox, tickedOf } from './review-parts.js'

export type WorkLine = AgreementPage['upcoming'][number]

const COLUMNS = 'grid-cols-[20px_56px_minmax(200px,1.3fr)_minmax(160px,1fr)_88px_minmax(150px,190px)_104px]'

const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000)

/**
 * The next twelve weeks of an agreement as a worklist, one line a job:
 * overdue first, then month by month. Visits at one site due close together
 * are one line, because they are one job — the server groups them with the
 * worker's own rule (`agreementWorklist`), and Make job sends its line's
 * `scheduleId`, `sequence` and `with` to `repeat.createJob` as they came.
 *
 * Make job is on a line whose next visit is inside the lead and has no job
 * yet, and Make early on one that could be made but is not inside the lead;
 * one made already links to its job. Ticking a line ticks its services for
 * the bulk bar, like the other looks.
 */
export function ComingUp({
  page,
  lines,
  actions,
  onMake,
  making,
}: {
  page: AgreementPage
  /** The lines to draw, already narrowed with the rest of the page. */
  lines: readonly WorkLine[]
  actions: ReviewActions
  /** Absent for somebody who cannot make jobs, or on an agreement that is not running. */
  onMake?: (lines: readonly WorkLine[]) => void
  /** The keys of lines being made now. */
  making: ReadonlySet<string>
}) {
  const jobHref = useJobHref()
  const { selected, onSelect } = actions
  const lead = leadLabel(page.leadDays)
  const ready = lines.filter((line) => line.ready)
  const together = combineLabel(page)

  const sections = new Map<string, WorkLine[]>()
  for (const line of lines) {
    const key = !line.job && line.state === 'OVERDUE' ? 'late' : line.on.slice(0, 7)
    sections.set(key, [...(sections.get(key) ?? []), line])
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 pb-2 text-xs text-ink-600">
        <span className="min-w-0 flex-1">
          {[
            'The next 12 weeks, and anything late.',
            together ? `Visits at one site due ${together} are one job.` : null,
            page.autoCreate
              ? `Jobs are created ${lead} before they’re due.`
              : `Jobs are made by hand from ${lead} before they’re due.`,
          ]
            .filter(Boolean)
            .join(' ')}
        </span>
        {onMake && ready.length > 0 ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" className="size-3.5" />}
            loading={ready.some((line) => making.has(line.key))}
            onClick={() => onMake(ready)}
          >
            {`Make ${ready.length} job${ready.length === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </div>

      {lines.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-ink-500">Nothing is due in the next 12 weeks.</p>
      ) : (
        <div className="overflow-x-auto">
          <div role="table" aria-label="Coming up" className="min-w-[860px]">
            {[...sections.entries()].map(([key, section]) => (
              <div key={key} role="rowgroup">
                <div role="row" className="flex items-baseline gap-2 border-t border-ink-100 bg-ink-50/70 px-2 pt-2.5 pb-1.5">
                  <span role="rowheader" className={cn('text-[12.5px] font-semibold', key === 'late' ? 'text-red-700' : 'text-ink-900')}>
                    {key === 'late' ? 'Overdue' : monthHeading(key)}
                  </span>
                  <span className="text-xs text-ink-500">
                    {`${section.length} job${section.length === 1 ? '' : 's'} · ${money(section.reduce((sum, line) => sum + line.priceIncTax, 0))}`}
                  </span>
                </div>
                {section.map((line) => {
                  const site = siteTitle(line.site, page.client.id)
                  const services = line.services.join(' + ')
                  const label = `${services} at ${site.name}`
                  const late = !line.job && line.state === 'OVERDUE'
                  return (
                    <div
                      key={line.key}
                      role="row"
                      aria-label={label}
                      className={cn('grid items-center gap-3 border-t border-ink-100 px-2 py-[var(--row-pad-y)] hover:bg-ink-50', COLUMNS)}
                    >
                      <span role="cell" className="flex">
                        {selected && onSelect ? (
                          <TickBox
                            checked={tickedOf(line.scheduleIds, selected)}
                            onChange={(checked) => onSelect(line.scheduleIds, checked)}
                            label={`Select ${label}`}
                          />
                        ) : null}
                      </span>
                      <span
                        role="cell"
                        className={cn(
                          'flex items-baseline justify-center gap-1 rounded-md py-0.5',
                          late ? 'bg-red-50 text-red-700' : line.job ? 'bg-blue-50 text-blue-800' : 'bg-ink-100 text-ink-700',
                        )}
                      >
                        <span className="text-[10px] uppercase">{weekday(line.on)}</span>
                        <span className="text-[13px] font-semibold tabular-nums">{Number(line.on.slice(8))}</span>
                      </span>
                      <span role="cell" className="min-w-0 truncate text-[13px]" title={[site.name, site.address].filter(Boolean).join(' · ')}>
                        <span className="font-medium text-ink-900">{site.name}</span>
                        {site.address ? <span className="text-ink-400">{` ${site.address}`}</span> : null}
                      </span>
                      <span role="cell" className="min-w-0 truncate text-[12.5px] text-ink-700" title={services}>
                        {services}
                      </span>
                      <span role="cell" className="text-right text-[12.5px] text-ink-700 tabular-nums">
                        {line.priceIncTax ? money(line.priceIncTax) : <span className="text-amber-700">No price</span>}
                      </span>
                      <span role="cell" className="min-w-0 truncate text-[12.5px]">
                        {line.job ? (
                          <>
                            <Link to={jobHref(line.job.id)} className="font-medium text-blue-700 hover:underline">
                              {`Job ${formatJobNumber(line.job)}`}
                            </Link>
                            <span className="text-ink-500">
                              {line.job.bookedOn ? ` · booked ${dayLabel(line.job.bookedOn, { year: false })}` : ' · not booked yet'}
                            </span>
                          </>
                        ) : late ? (
                          <span className="font-medium text-red-700">{lateLabel(line.dueOn, page.today)}</span>
                        ) : line.ready ? (
                          <span className="font-medium text-brand-700">Ready for a job</span>
                        ) : line.refusal ? (
                          <span className="text-ink-500">{line.refusal}</span>
                        ) : (
                          <span className="text-ink-500">
                            {`${page.autoCreate ? 'Made' : 'Can be made'} from ${dayLabel(line.createOn, { year: false })}`}
                          </span>
                        )}
                      </span>
                      <span role="cell" className="flex justify-end">
                        {onMake && line.ready ? (
                          <Button
                            size="sm"
                            icon={<Icon name="plus" className="size-3.5" />}
                            loading={making.has(line.key)}
                            aria-label={`Make the job for ${label}`}
                            onClick={() => onMake([line])}
                          >
                            Make job
                          </Button>
                        ) : onMake && line.canMake && !line.job ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={making.has(line.key)}
                            aria-label={`Make the job for ${label} early`}
                            onClick={() => onMake([line])}
                          >
                            Make early
                          </Button>
                        ) : null}
                      </span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function monthHeading(month: string): string {
  return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' }).format(new Date(`${month}-15T12:00:00`))
}

function weekday(day: string): string {
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short' }).format(new Date(`${day}T12:00:00`))
}

function lateLabel(dueOn: string, today: string): string {
  const days = daysBetween(dueOn, today)
  return days <= 0 ? `Due ${dayLabel(dueOn, { year: false })}` : `${days} day${days === 1 ? '' : 's'} late`
}

/** The lines the page's narrowing leaves: any of whose services is still shown. */
export function linesShown(lines: readonly WorkLine[], shownIds: ReadonlySet<string>): WorkLine[] {
  return lines.filter((line) => line.scheduleIds.some((id) => shownIds.has(id)))
}

